// Engagement types and billing presets — the bounded vocabulary.
//
// TYPE says what we deliver (Alex, 29 Oct 2026): `coaching` is sessions sold
// and counted as a block; `programme` is a monthly retainer with coaching
// around it (a draw-down fund, workshops, Pulse). Monthly retained coaching
// is the smallest programme. The type is STORED on the engagement for
// reporting and tells Setup which modules to switch on. Rendering never
// reads it: declared modules only. See .claude/skills/engagement-modules.
//
// BILLING says how we charge, and is independent of the type: New Zealand
// (NZD, plus GST, invoiced in Xero) or international (the client's currency,
// no GST, invoiced from the Hub and re-keyed into Xero once paid, so the
// NZD figure matches what landed). Stored at the top of Finance Config as
// `currency`, `tax` and `billing: { region, invoicing }`.
//
// Module bodies are deliberately NOT auto-created at onboarding: an empty
// sessionBlock would render "Block complete", an empty retainer "$0/mo".

export const ENGAGEMENT_TYPES = {
  coaching: {
    label: "Coaching",
    modules: ["sessionBlock", "progressPhases"],
  },
  programme: {
    label: "Programme",
    modules: ["retainer", "coachingRoster", "drawdownPool", "rateCard", "progressPhases"],
  },
};

// Keys stored before 29 Oct 2026 (Amalga and Corporate Cabs were
// `coachingBlock`, 36 Degrees `retainerCoaching`). Read, never written.
export const LEGACY_TYPES = { coachingBlock: "coaching", retainerCoaching: "programme" };
export const typeKey = v => LEGACY_TYPES[v] || v || "";
export const typeLabel = v => ENGAGEMENT_TYPES[typeKey(v)]?.label || v || "";

export const REGIONS = {
  nz: { label: "New Zealand", currency: "NZD", tax: "gst", invoicing: "xero" },
  international: { label: "International", currency: "USD", tax: "none", invoicing: "hub" },
};
export const INVOICING = { xero: "Xero", hub: "Hub (generate here, then Xero once paid)" };
export const CURRENCIES = ["NZD", "USD", "GBP", "AUD"];

// Standard rate cards by currency, used to seed an engagement's rate card
// when the module is switched on. NZD is the house list (36 Degrees' card,
// per hour); USD is the Schedule A unit prices agreed with Keynes.
export const RATE_CARDS = {
  NZD: [
    { id: "leadership-coach", label: "Leadership coach", rate: 550 },
    { id: "team-coach", label: "Team coach", rate: 330 },
    { id: "strategy-consultant", label: "Strategy consultant", rate: 410 },
    { id: "research-analyst", label: "Research analyst", rate: 200 },
  ],
  USD: [
    { id: "lead", label: "Leadership coaching", rate: 285, unit: "/seat/mo" },
    { id: "team", label: "Team Safe Space coaching", rate: 195, unit: "/seat/mo" },
    { id: "saa", label: "Strategic Accountability & Alignment", rate: 625, unit: "/mo" },
    { id: "drawdown", label: "Leadership Draw-Down", rate: 500, unit: "/mo" },
    { id: "workshops", label: "Targeted Workshops (annualised)", rate: 1000, unit: "/mo" },
    { id: "pulse", label: "Culture & Sentiment (Pulse)", rate: 350, unit: "/mo" },
    { id: "disc", label: "DiSC profile", rate: 190, unit: " each" },
    { id: "fep-checkin", label: "Fresh Eyes Pulse check-in", rate: 250, unit: "/check-in" },
  ],
};

// Region from a config's stored fields: explicit `billing.region`, else
// NZD means New Zealand and anything else international.
export function regionOf(raw) {
  const r = raw || {};
  if (r.billing?.region && REGIONS[r.billing.region]) return r.billing.region;
  return (r.currency || "NZD") === "NZD" ? "nz" : "international";
}
export function invoicingOf(raw) {
  const r = raw || {};
  if (r.billing?.invoicing && INVOICING[r.billing.invoicing]) return r.billing.invoicing;
  return REGIONS[regionOf(r)].invoicing;
}

export function initialConfig(type, engagementStart, region = "nz") {
  const key = typeKey(type);
  if (!ENGAGEMENT_TYPES[key]) throw new Error(`Unknown engagement type: ${type}`);
  const reg = REGIONS[region] || REGIONS.nz;
  return { engagementType: key, engagementStart, currency: reg.currency, tax: reg.tax, billing: { region: REGIONS[region] ? region : "nz", invoicing: reg.invoicing }, modules: {} };
}
